import React, { useState, useEffect } from "react";
import { TextField, Select, Button, Table } from "../ui";
import { FeatherChevronDown, FeatherCheck } from "@subframe/core";
import { getAllCustomers, getCustomerById } from "../services/customerApi";
import { fetchCustomerPayments, CustomerPayment } from "../services/api";
import { SearchableDropdown } from "../shared/presentation/components";
import { useDropdownCache } from "../shared/presentation/hooks";
import { getStoredAccountingBackend } from "../config/accounting";

interface SelectedPayment {
  payment: CustomerPayment;
  amount: number;
  isSelected: boolean;
}

interface VendorCustomerPaymentRefundRecoProps {
  transactionDate: string;
  transactionAmount: number;
  onCustomerChange: (customer: {
    customer_id: string;
    customer_name: string;
  }) => void;
  onDateChange: (date: string) => void;
  onReferenceNumberChange: (refNumber: string) => void;
  onPaymentModeChange: (paymentMode: string) => void;
  onSelectedPaymentsChange: (
    payment: {
      payment_id: string;
      payment_number: string;
      amount: number;
    } | null
  ) => void;
}

const VendorCustomerPaymentRefundReco: React.FC<
  VendorCustomerPaymentRefundRecoProps
> = ({
  transactionDate,
  transactionAmount,
  onCustomerChange,
  onDateChange,
  onReferenceNumberChange,
  onPaymentModeChange,
  onSelectedPaymentsChange,
}) => {
  const isTally = getStoredAccountingBackend() === "tally";
  const [selectedCustomer, setSelectedCustomer] = useState<{
    customer_id: string;
    customer_name: string;
  } | null>(null);
  const [date, setDate] = useState<string>("");
  const [referenceNumber, setReferenceNumber] = useState<string>("");
  const [paymentMode, setPaymentMode] = useState<string>("Bank Transfer");
  const [customerPayments, setCustomerPayments] = useState<CustomerPayment[]>(
    []
  );
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [selectedPayments, setSelectedPayments] = useState<SelectedPayment[]>(
    []
  );

  // Dropdown cache hook
  const { getCached, setCached } = useDropdownCache();

  // Cache for selected customers (to avoid refetching)
  const [customersCache, setCustomersCache] = useState<Map<string, any>>(
    new Map()
  );

  // Initialize date with transaction date
  useEffect(() => {
    if (transactionDate) {
      const formattedDate = new Date(transactionDate)
        .toISOString()
        .split("T")[0];
      setDate(formattedDate);
      onDateChange(formattedDate);
    }
  }, [transactionDate, onDateChange]);

  // Initialize payment mode
  useEffect(() => {
    onPaymentModeChange(paymentMode);
  }, [paymentMode, onPaymentModeChange]);

  // Fetch customer payments when customer is selected (Zoho only)
  const fetchCustomerPaymentsForCustomer = async (customerId: string) => {
    try {
      setLoadingPayments(true);
      const response = await fetchCustomerPayments(customerId);

      if (response.success) {
        const paymentsWithBalance = response.data.payments;
        setCustomerPayments(paymentsWithBalance);

        // Initialize selected payments
        const initialSelectedPayments = paymentsWithBalance.map(
          (payment: CustomerPayment) => ({
            payment,
            amount: 0,
            isSelected: false,
          })
        );
        setSelectedPayments(initialSelectedPayments);
      }
    } catch (error) {
      console.error("Failed to fetch customer payments:", error);
      setCustomerPayments([]);
      setSelectedPayments([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleCustomerSelect = (customer: any) => {
    const customerId = customer.zoho_customerid;
    const customerData = {
      customer_id: customerId,
      customer_name: customer.customer_name,
    };
    setSelectedCustomer(customerData);
    onCustomerChange(customerData);
    if (isTally) {
      // Tally posts a simple Payment against the customer ledger — no Zoho payment list.
      setCustomerPayments([]);
      setSelectedPayments([]);
      onSelectedPaymentsChange({
        payment_id: "",
        payment_number: "",
        amount: transactionAmount,
      });
    } else {
      fetchCustomerPaymentsForCustomer(customerId);
    }

    // Cache the selected customer for future use
    setCustomersCache((prev) => {
      const newCache = new Map(prev);
      newCache.set(customerId, customer);
      return newCache;
    });

    // Cache the selected customer in the dropdown cache
    const cacheKey = { searchTerm: "", limit: 20, offset: 0 };
    const cached = getCached<{
      items: any[];
      hasMore?: boolean;
    }>("customers", cacheKey);

    if (cached) {
      // Check if customer is already in cache
      const hasCustomer = cached.items.some(
        (c: any) => c.zoho_customerid === customerId
      );
      if (!hasCustomer) {
        // Prepend selected customer to cached results
        setCached("customers", cacheKey, {
          items: [customer, ...cached.items],
          hasMore: cached.hasMore,
        });
      }
    }
  };

  // Fetch customers async function for SearchableDropdown
  const fetchCustomers = async (
    searchTerm: string,
    limit: number,
    offset: number,
    selectedCustomerId?: string,
    selectedCustomerName?: string
  ): Promise<{
    items: any[];
    hasMore?: boolean;
  }> => {
    // Only cache the initial load (offset 0, no search)
    const shouldCache = offset === 0 && !searchTerm;
    const cacheKey = { searchTerm, limit, offset };

    if (shouldCache) {
      const cached = getCached<{
        items: any[];
        hasMore?: boolean;
      }>("customers", cacheKey);

      if (cached) {
        // Always ensure selected customer is in the cached results (if provided)
        if (offset === 0 && selectedCustomerId && selectedCustomerName) {
          const hasSelected = cached.items.some(
            (customer: any) => customer.zoho_customerid === selectedCustomerId
          );

          if (!hasSelected) {
            // Check customers cache first (pre-fetched customers)
            const cachedCustomer = customersCache.get(selectedCustomerId);
            if (cachedCustomer) {
              return {
                items: [cachedCustomer, ...cached.items],
                hasMore: cached.hasMore,
              };
            }

            // Try to fetch selected customer from backend
            try {
              const selectedResponse = await getCustomerById(
                selectedCustomerId
              );
              if (selectedResponse.data) {
                // Map the response data to customer format
                const mappedCustomer: any = {
                  zoho_customerid: selectedResponse.data.zoho_customerid,
                  customer_id: selectedResponse.data.customer_id,
                  customer_name: selectedResponse.data.customer_name,
                  company_name: selectedResponse.data.customer_name,
                  email: selectedResponse.data.contact_email_1 || "",
                  phone: selectedResponse.data.contact_number_1 || "",
                  status: selectedResponse.data.status,
                  billing_address: selectedResponse.data.billing_address,
                  shipping_address: selectedResponse.data.shipping_address,
                  currency_code: selectedResponse.data.currency_id || "",
                  payment_terms: selectedResponse.data.payment_terms,
                  credit_limit: selectedResponse.data.credit_limit || 0,
                  created_at: selectedResponse.data.created_at,
                  updated_at: selectedResponse.data.updated_at,
                };
                // Store in cache for future use
                setCustomersCache((prev) => {
                  const newCache = new Map(prev);
                  newCache.set(selectedCustomerId, mappedCustomer);
                  return newCache;
                });
                return {
                  items: [mappedCustomer, ...cached.items],
                  hasMore: cached.hasMore,
                };
              }
            } catch (error) {
              console.warn(
                "Could not fetch selected customer from backend, using synthetic item:",
                error
              );
            }

            // Fallback: Create synthetic customer
            const syntheticCustomer: any = {
              zoho_customerid: selectedCustomerId,
              customer_id: selectedCustomerId,
              customer_name: selectedCustomerName,
              company_name: selectedCustomerName,
              email: "",
              phone: "",
              status: "active",
              billing_address: null,
              shipping_address: null,
              currency_code: "",
              payment_terms: "",
              credit_limit: 0,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
            return {
              items: [syntheticCustomer, ...cached.items],
              hasMore: cached.hasMore,
            };
          } else {
            // Selected customer exists in cache, move it to top for better UX
            const selectedIndex = cached.items.findIndex(
              (customer: any) => customer.zoho_customerid === selectedCustomerId
            );
            if (selectedIndex > 0) {
              const itemsCopy = [...cached.items];
              const [selectedCustomer] = itemsCopy.splice(selectedIndex, 1);
              return {
                items: [selectedCustomer, ...itemsCopy],
                hasMore: cached.hasMore,
              };
            }
          }
        }
        return cached;
      }
    }

    try {
      const response = await getAllCustomers({
        search: searchTerm || undefined,
        limit,
        offset,
      });

      let items = response.data || [];

      // Always ensure selected customer is in results (if provided)
      if (offset === 0 && selectedCustomerId && selectedCustomerName) {
        const hasSelected = items.some(
          (customer: any) => customer.zoho_customerid === selectedCustomerId
        );

        if (!hasSelected) {
          // Check customers cache first
          const cachedCustomer = customersCache.get(selectedCustomerId);
          if (cachedCustomer) {
            items = [cachedCustomer, ...items];
          } else {
            // Try to fetch selected customer from backend
            try {
              const selectedResponse = await getCustomerById(
                selectedCustomerId
              );
              if (selectedResponse.data) {
                // Map the response data to customer format
                const mappedCustomer: any = {
                  zoho_customerid: selectedResponse.data.zoho_customerid,
                  customer_id: selectedResponse.data.customer_id,
                  customer_name: selectedResponse.data.customer_name,
                  company_name: selectedResponse.data.customer_name,
                  email: selectedResponse.data.contact_email_1 || "",
                  phone: selectedResponse.data.contact_number_1 || "",
                  status: selectedResponse.data.status,
                  billing_address: selectedResponse.data.billing_address,
                  shipping_address: selectedResponse.data.shipping_address,
                  currency_code: selectedResponse.data.currency_id || "",
                  payment_terms: selectedResponse.data.payment_terms,
                  credit_limit: selectedResponse.data.credit_limit || 0,
                  created_at: selectedResponse.data.created_at,
                  updated_at: selectedResponse.data.updated_at,
                };
                // Store in cache for future use
                setCustomersCache((prev) => {
                  const newCache = new Map(prev);
                  newCache.set(selectedCustomerId, mappedCustomer);
                  return newCache;
                });
                items = [mappedCustomer, ...items];
              } else {
                // Fallback: Create synthetic customer
                const syntheticCustomer: any = {
                  zoho_customerid: selectedCustomerId,
                  customer_id: selectedCustomerId,
                  customer_name: selectedCustomerName,
                  company_name: selectedCustomerName,
                  email: "",
                  phone: "",
                  status: "active",
                  billing_address: null,
                  shipping_address: null,
                  currency_code: "",
                  payment_terms: "",
                  credit_limit: 0,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                };
                items = [syntheticCustomer, ...items];
              }
            } catch (error) {
              console.warn(
                "Could not fetch selected customer from backend, using synthetic item:",
                error
              );
              // Fallback: Create synthetic customer
              const syntheticCustomer: any = {
                zoho_customerid: selectedCustomerId,
                customer_id: selectedCustomerId,
                customer_name: selectedCustomerName,
                company_name: selectedCustomerName,
                email: "",
                phone: "",
                status: "active",
                billing_address: null,
                shipping_address: null,
                currency_code: "",
                payment_terms: "",
                credit_limit: 0,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              };
              items = [syntheticCustomer, ...items];
            }
          }
        } else {
          // If selected customer exists, move it to the top (remove from current position first)
          const selectedIndex = items.findIndex(
            (customer: any) => customer.zoho_customerid === selectedCustomerId
          );
          if (selectedIndex >= 0) {
            // Remove from current position and prepend to top
            const [selectedCustomer] = items.splice(selectedIndex, 1);
            items = [selectedCustomer, ...items];
          }
        }
      }

      const result = {
        items,
        hasMore: response.pagination?.has_more || false,
      };

      // Cache the BASE results (without selected item prepended)
      if (shouldCache) {
        setCached("customers", cacheKey, {
          items: response.data || [],
          hasMore: response.pagination?.has_more || false,
        });
      }

      return result;
    } catch (error) {
      console.error("Error fetching customers:", error);
      return {
        items: [],
        hasMore: false,
      };
    }
  };

  const handleDateChange = (newDate: string) => {
    setDate(newDate);
    onDateChange(newDate);
  };

  const handleReferenceNumberChange = (refNum: string) => {
    setReferenceNumber(refNum);
    onReferenceNumberChange(refNum);
  };

  const handlePaymentModeChange = (mode: string) => {
    setPaymentMode(mode);
    onPaymentModeChange(mode);
  };

  const handlePaymentSelect = (paymentId: string) => {
    setSelectedPayments((prev) => {
      // Only allow one payment to be selected at a time
      const updated = prev.map((item) => {
        if (item.payment.payment_id === paymentId) {
          const newIsSelected = !item.isSelected;
          const newAmount = newIsSelected
            ? item.payment.unused_amount > transactionAmount
              ? transactionAmount
              : item.payment.unused_amount
            : 0;
          return {
            ...item,
            isSelected: newIsSelected,
            amount: newAmount,
          };
        }
        // Deselect all other payments
        return { ...item, isSelected: false, amount: 0 };
      });

      // Notify parent about selected payment (single payment object or null)
      const selected = updated.find(
        (item) => item.isSelected && item.amount > 0
      );

      if (selected) {
        onSelectedPaymentsChange({
          payment_id: selected.payment.payment_id,
          payment_number: selected.payment.payment_number,
          amount: selected.amount,
        });
      } else {
        onSelectedPaymentsChange(null);
      }

      return updated;
    });
  };

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="flex w-full flex-col items-start gap-2">
        <div className="flex w-full items-center gap-4">
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Customer <span className="text-error-600">*</span>
            </span>
            <SearchableDropdown
              fetchItems={(searchTerm, limit, offset) =>
                fetchCustomers(
                  searchTerm,
                  limit,
                  offset,
                  selectedCustomer?.customer_id,
                  selectedCustomer?.customer_name
                )
              }
              trigger={
                <Button
                  className="w-full justify-between"
                  variant="neutral-secondary"
                  iconRight={<FeatherChevronDown />}
                >
                  <span className="truncate text-left">
                    {selectedCustomer
                      ? selectedCustomer.customer_name
                      : "Select customer"}
                  </span>
                </Button>
              }
              getSearchableText={(customer) => customer.customer_name}
              renderItem={(customer) => customer.customer_name}
              getItemKey={(customer) => customer.zoho_customerid}
              onSelect={handleCustomerSelect}
              selectedKey={selectedCustomer?.customer_id}
              searchPlaceholder="Search customers..."
              emptyMessage="No customers found"
              noResultsMessage="No customers match your search"
              dropdownClassName="min-w-[300px]"
              initialLimit={20}
            />
          </div>
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Date <span className="text-error-600">*</span>
            </span>
            <TextField className="w-full" label="" helpText="">
              <TextField.Input
                type="date"
                value={date}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleDateChange(event.target.value)
                }
              />
            </TextField>
          </div>
        </div>
        <div className="flex w-full items-center gap-4">
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Reference #{" "}
              {isTally && <span className="text-error-600">*</span>}
            </span>
            <TextField className="w-full" label="" helpText="">
              <TextField.Input
                placeholder={
                  isTally
                    ? "Enter reference number (required)"
                    : "Enter reference number (optional)"
                }
                value={referenceNumber}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleReferenceNumberChange(event.target.value)
                }
              />
            </TextField>
          </div>
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Paid Via <span className="text-error-600">*</span>
            </span>
            <Select
              label=""
              helpText=""
              value={paymentMode}
              onValueChange={handlePaymentModeChange}
            >
              <Select.Item value="Bank Remittance">Bank Remittance</Select.Item>
              <Select.Item value="Bank Transfer">Bank Transfer</Select.Item>
              <Select.Item value="Cash">Cash</Select.Item>
              <Select.Item value="Cheque">Cheque</Select.Item>
              <Select.Item value="Credit Card">Credit Card</Select.Item>
              <Select.Item value="UPI">UPI</Select.Item>
            </Select>
          </div>
        </div>
      </div>

      {/* Tally: no Zoho payment picker — refund amount is the bank transaction */}
      {isTally && selectedCustomer && (
        <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-4">
          <span className="text-body-bold font-body-bold text-default-font">
            Refund Amount
          </span>
          <span className="text-body font-body text-default-font">
            ₹{transactionAmount.toLocaleString("en-IN")} will be posted as a
            Payment against {selectedCustomer.customer_name}
          </span>
        </div>
      )}

      {/* Customer Payments Table (Zoho only) */}
      {!isTally && selectedCustomer && (
        <div className="flex w-full flex-col items-start gap-2">
          <div className="flex w-full items-center justify-between">
            <span className="text-body-bold font-body-bold text-default-font">
              Select Payment <span className="text-error-600">*</span>
            </span>
            <div className="flex items-center gap-2">
              <span className="text-caption font-caption text-neutral-400">
                Transaction Amount:
              </span>
              <span className="text-caption font-caption text-default-font">
                ₹{transactionAmount.toLocaleString("en-IN")}
              </span>
            </div>
          </div>
          {loadingPayments ? (
            <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-6">
              <span className="text-body font-body text-neutral-500">
                Loading payments...
              </span>
            </div>
          ) : customerPayments.length > 0 ? (
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell className="w-[150px]">
                    Payment #
                  </Table.HeaderCell>
                  <Table.HeaderCell className="w-[120px]">
                    Date
                  </Table.HeaderCell>
                  <Table.HeaderCell className="w-[130px]">
                    Amount
                  </Table.HeaderCell>
                  <Table.HeaderCell className="w-[130px]">
                    Balance
                  </Table.HeaderCell>
                  <Table.HeaderCell className="w-[140px]">
                    Action
                  </Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {selectedPayments.map((item) => (
                <Table.Row key={item.payment.payment_id}>
                  <Table.Cell className="w-[150px]">
                    <span className="text-body-bold font-body-bold text-default-font">
                      #{item.payment.payment_number}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="w-[120px]">
                    <span className="text-body font-body text-default-font">
                      {item.payment.date
                        ? new Date(item.payment.date)
                            .toLocaleDateString("en-GB")
                            .split("/")
                            .join("/")
                        : "—"}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="w-[130px]">
                    <span className="text-body font-body text-default-font">
                      ₹{item.payment.amount.toLocaleString("en-IN")}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="w-[130px]">
                    <span className="text-body font-body text-default-font">
                      ₹{item.payment.unused_amount.toLocaleString("en-IN")}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="w-[140px]">
                    <Button
                      className={
                        item.isSelected
                          ? "bg-success-600 text-white hover:bg-success-700 w-[120px]"
                          : "w-[120px]"
                      }
                      size="small"
                      icon={item.isSelected ? <FeatherCheck /> : undefined}
                      onClick={() =>
                        handlePaymentSelect(item.payment.payment_id)
                      }
                    >
                      {item.isSelected ? "Selected" : "Select"}
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table>
          ) : (
            <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-6">
              <span className="text-body font-body text-neutral-500">
                No payments found for this customer
              </span>
            </div>
          )}
        </div>
      )}

      {!isTally && !selectedCustomer && (
        <div className="flex w-full flex-col items-start gap-2">
          <span className="text-body-bold font-body-bold text-default-font">
            Select Payment <span className="text-error-600">*</span>
          </span>
          <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-6">
            <span className="text-body font-body text-neutral-500">
              Select a customer to view available payments
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorCustomerPaymentRefundReco;
